import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import { 
  Menu, 
  X, 
  ArrowRight,
  Users,
  Globe,
  Award
} from "lucide-react";
import { Container, PrimaryButton } from "../styles/GlobalStyles";

// Modern Header Component
const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid ${props => props.scrolled ? 'var(--gray-200)' : 'rgba(255, 255, 255, 0.1)'};
  z-index: 1000;
  transition: all var(--transition-normal);
  box-shadow: ${props => props.scrolled ? 'var(--shadow-lg)' : 'none'};
`;

const HeaderContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-6);
  
  @media (max-width: 1024px) {
    padding: var(--space-3) var(--space-5);
  }
  
  @media (max-width: 768px) {
    padding: var(--space-3) var(--space-4);
  }
  
  @media (max-width: 480px) {
    padding: var(--space-2) var(--space-3);
  }
`;

const Logo = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  text-decoration: none;
  font-weight: 800;
  font-size: var(--text-xl);
  color: var(--gray-900);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &:hover {
    transform: scale(1.08) translateY(-2px);
    filter: brightness(1.15) saturate(1.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: -8px;
    right: -8px;
    bottom: -8px;
    background: linear-gradient(135deg, 
      rgba(59, 130, 246, 0.1) 0%, 
      rgba(147, 51, 234, 0.08) 25%,
      rgba(236, 72, 153, 0.06) 50%,
      rgba(59, 130, 246, 0.08) 75%,
      rgba(147, 51, 234, 0.1) 100%
    );
    border-radius: var(--radius-2xl);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: -1;
    animation: pulse-glow 3s ease-in-out infinite;
  }
  
  &:hover::before {
    opacity: 1;
    transform: scale(1.05);
  }
  
  @keyframes pulse-glow {
    0%, 100% { 
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
    50% { 
      box-shadow: 0 0 20px 0 rgba(59, 130, 246, 0.1);
    }
  }
  
  .logo-image {
    width: 52px;
    height: 52px;
    object-fit: contain;
    border-radius: var(--radius-full);
    box-shadow: 
      0 4px 20px rgba(59, 130, 246, 0.25),
      0 8px 32px rgba(147, 51, 234, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, 
      #3b82f6 0%, 
      #8b5cf6 25%, 
      #ec4899 50%, 
      #3b82f6 75%, 
      #8b5cf6 100%
    );
    background-size: 200% 200%;
    padding: var(--space-2);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.4), 
        transparent
      );
      transition: left 0.6s ease;
    }
    
    &:hover {
      box-shadow: 
        0 8px 32px rgba(59, 130, 246, 0.4),
        0 16px 64px rgba(147, 51, 234, 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
      transform: rotate(8deg) scale(1.1);
      background-position: 100% 100%;
      
      &::before {
        left: 100%;
      }
    }
    
    @media (max-width: 768px) {
      width: 44px;
      height: 44px;
      padding: var(--space-1);
    }
    
    @media (max-width: 480px) {
      width: 40px;
      height: 40px;
    }
  }
  
  .logo-content {
    display: flex;
    flex-direction: column;
    gap: 0;
    position: relative;
  }
  
  .logo-text {
    font-weight: 900;
    font-size: var(--text-2xl);
    background: linear-gradient(135deg, 
      #3b82f6 0%, 
      #8b5cf6 25%, 
      #ec4899 50%, 
      #f59e0b 75%, 
      #3b82f6 100%
    );
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.03em;
    position: relative;
    line-height: 1;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    text-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 0;
      height: 3px;
      background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
      border-radius: var(--radius-full);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
    }
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, 
        rgba(59, 130, 246, 0.05) 0%, 
        rgba(147, 51, 234, 0.05) 50%, 
        rgba(236, 72, 153, 0.05) 100%
      );
      border-radius: var(--radius-lg);
      opacity: 0;
      transition: opacity 0.4s ease;
      z-index: -1;
    }
    
    @media (max-width: 768px) {
      font-size: var(--text-xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-lg);
    }
  }
  
  .tagline {
    font-size: var(--text-xs);
    background: linear-gradient(90deg, #6b7280, #9333ea, #6b7280);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 600;
    margin-top: -1px;
    font-style: italic;
    line-height: 1;
    opacity: 0.8;
    transition: all 0.3s ease;
    transform: translateY(2px);
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &:hover {
    .logo-text {
      background-position: 100% 100%;
      transform: scale(1.02);
      
      &::after {
        width: 100%;
      }
      
      &::before {
        opacity: 1;
      }
    }
    
    .tagline {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: var(--space-8);
  
  @media (max-width: 1024px) {
    gap: var(--space-6);
  }
  
  @media (max-width: 1200px) {
    gap: var(--space-4);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  align-items: center;
  gap: var(--space-6);
  list-style: none;
  margin: 0;
  padding: 0;
  
  @media (max-width: 1200px) {
    gap: var(--space-4);
  }
  
  @media (max-width: 1024px) {
    gap: var(--space-3);
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-xl);
  font-weight: 600;
  font-size: var(--text-sm);
  color: var(--gray-700);
  text-decoration: none;
  transition: all var(--transition-fast);
  position: relative;
  white-space: nowrap;
  
  @media (max-width: 1200px) {
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-xs);
    font-weight: 500;
  }
  
  @media (max-width: 1024px) {
    padding: var(--space-2);
    font-size: var(--text-xs);
  }
  
  &:hover, &.active {
    color: var(--primary-600);
    background: var(--primary-50);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
  
  &.active::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 2px;
    background: var(--primary-600);
    border-radius: var(--radius-full);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  
  @media (max-width: 1024px) {
    gap: var(--space-3);
  }
  
  @media (max-width: 768px) {
    gap: var(--space-2);
  }
  
  @media (max-width: 480px) {
    gap: var(--space-1);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  background: var(--gray-100);
  border-radius: var(--radius-lg);
  color: var(--gray-700);
  transition: all var(--transition-fast);
  cursor: pointer;
  
  &:hover {
    background: var(--gray-200);
    color: var(--gray-900);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: none;
  background: var(--gray-50);
  border-radius: var(--radius-full);
  color: var(--gray-600);
  transition: all var(--transition-fast);
  cursor: pointer;
  position: relative;
  
  &:hover {
    background: var(--primary-50);
    color: var(--primary-600);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: var(--radius-full);
    padding: 2px;
    background: linear-gradient(135deg, var(--primary-400), var(--blue-500));
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: subtract;
    opacity: 0;
    transition: opacity var(--transition-fast);
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--gray-0);
  z-index: 9999;
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6) var(--space-4);
  border-bottom: 1px solid var(--gray-100);
  background: var(--gray-0);
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(10px);
`;

const MobileMenuContent = styled.div`
  padding: var(--space-6) var(--space-4);
  background: linear-gradient(180deg, var(--gray-0) 0%, var(--gray-25) 100%);
  min-height: calc(100vh - 80px);
`;

const MobileNavItem = styled(RouterLink)`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-xl);
  color: var(--gray-700);
  text-decoration: none;
  margin-bottom: var(--space-3);
  font-weight: 500;
  font-size: var(--text-base);
  transition: all var(--transition-fast);
  background: var(--gray-0);
  border: 1px solid var(--gray-100);
  
  &:hover {
    background: var(--primary-50);
    color: var(--primary-600);
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    border-color: var(--primary-200);
  }
  
  &.active {
    background: var(--gradient-primary);
    color: var(--gray-0);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
    border-color: transparent;
  }
  
  .icon {
    width: 24px;
    height: 24px;
    opacity: 0.8;
  }
  
  &:hover .icon,
  &.active .icon {
    opacity: 1;
  }
`;

const PromoBar = styled.div`
  background: linear-gradient(135deg, 
    #3b82f6 0%, 
    #8b5cf6 25%, 
    #ec4899 50%, 
    #f59e0b 75%, 
    #3b82f6 100%
  );
  background-size: 300% 300%;
  animation: gradient-shift 8s ease infinite;
  color: var(--gray-0);
  padding: var(--space-3) 0;
  text-align: center;
  font-size: var(--text-sm);
  font-weight: 600;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 20px rgba(59, 130, 246, 0.3);
  
  @keyframes gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    animation: shimmer 4s infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255, 255, 255, 0.3), 
      transparent
    );
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  
  span {
    position: relative;
    z-index: 1;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    
    &::before {
      content: '✨';
      margin-right: var(--space-2);
      animation: pulse 2s ease-in-out infinite;
    }
    
    &::after {
      content: '✨';
      margin-left: var(--space-2);
      animation: pulse 2s ease-in-out infinite 1s;
    }
  }
  
  @keyframes pulse {
    0%, 100% { 
      opacity: 0.7;
      transform: scale(1);
    }
    50% { 
      opacity: 1;
      transform: scale(1.1);
    }
  }
`;

const navigationItems = [
  {
    label: 'Home',
    path: '/',
    icon: <Globe size={20} />
  },
  {
    label: 'About',
    path: '/about',
    icon: <Users size={20} />
  },
  {
    label: 'Services',
    path: '/service',
    icon: <Award size={20} />
  },
  {
    label: 'Programs',
    path: '/career-paths',
    icon: <Award size={20} />
  },
  {
    label: 'Internships',
    path: '/internships',
    icon: <Users size={20} />
  },
  {
    label: 'Contact',
    path: '/contact',
    icon: <Globe size={20} />
  }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <PromoBar>
        <Container>
          <span>New Feature Alert! Try our AI-powered analytics dashboard - Free for 30 days</span>
        </Container>
      </PromoBar>
      
      <HeaderWrapper scrolled={isScrolled}>
        <HeaderContainer>
          <Logo to="/">
            <img 
              src="/W_Logo.png" 
              alt="Willovate" 
              className="logo-image"
            />
            <div className="logo-content">
              <span className="logo-text">Willovate</span>
              <span className="tagline">Innovate with Heart</span>
            </div>
          </Logo>

          <Navigation>
            <NavMenu>
              {navigationItems.map((item) => (
                <NavItem key={item.path}>
                  <NavLink 
                    to={item.path}
                    className={({ isActive }) => isActive ? 'active' : ''}
                  >
                    {item.label}
                  </NavLink>
                </NavItem>
              ))}
            </NavMenu>
          </Navigation>

          <HeaderActions>
            <PrimaryButton onClick={() => window.open('https://willovateresto.com', '_blank')}>
              Our New Product
              <ArrowRight size={16} />
            </PrimaryButton>
            
            <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={20} />
            </MobileMenuButton>
          </HeaderActions>
        </HeaderContainer>
      </HeaderWrapper>

      <MobileMenuOverlay 
        isOpen={isMobileMenuOpen} 
        onClick={() => setIsMobileMenuOpen(false)} 
      />

      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileMenuHeader>
          <Logo to="/" onClick={() => setIsMobileMenuOpen(false)}>
            <img 
              src="/W_Logo.png" 
              alt="Willovate" 
              className="logo-image"
            />
            <div className="logo-content">
              <span className="logo-text">Willovate</span>
            </div>
          </Logo>
          
          <CloseButton 
            onClick={() => {
              setIsMobileMenuOpen(false);
            }}
          >
            <X size={24} />
          </CloseButton>
        </MobileMenuHeader>
        
        <MobileMenuContent>
          {navigationItems.map((item) => (
            <MobileNavItem 
              key={item.path} 
              to={item.path}
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="icon">{item.icon}</div>
              {item.label}
            </MobileNavItem>
          ))}
          
          <div style={{ marginTop: 'var(--space-8)' }}>
            <PrimaryButton 
              style={{ width: '100%' }}
              onClick={() => {
                setIsMobileMenuOpen(false);
                window.open('https://willovateresto.com', '_blank');
              }}
            >
              Our New Product
              <ArrowRight size={16} />
            </PrimaryButton>
          </div>
        </MobileMenuContent>
      </MobileMenu>
    </>
  );
};

export default Header;
